import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { AlertIcon } from '../../../assets/icons/index.tsx';
import PrivacyNotice from './PrivacyNotice';

// 8 dígitos (10^8 combinaciones) para que la fuerza bruta no sea viable;
// tiene que coincidir con GOTRUE_MAILER_OTP_LENGTH en el repo Homelab.
const OTP_LENGTH = 8;
const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Acceso sin contraseña: el usuario introduce su email, recibe un código de
 * un solo uso y lo verifica. Si el email no existe se crea la cuenta, así que
 * no hay pantalla de registro aparte.
 */
const LoginPage: React.FC = () => {
  const { sendOtp, verifyOtp, error, clearError } = useAuth();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const requestCode = async () => {
    setSubmitting(true);
    const ok = await sendOtp(email.trim());
    setSubmitting(false);
    if (ok) {
      setStep('code');
      setCode('');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await requestCode();
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await verifyOtp(email.trim(), code);
    // Si es correcto, onAuthStateChange cambia de pantalla y este componente se desmonta
    if (!ok) setSubmitting(false);
  };

  const changeEmail = () => {
    clearError();
    setStep('email');
    setCode('');
  };

  const inputClass =
    'w-full px-4 py-3 rounded-lg text-white placeholder-[#64748b] outline-none transition-colors bg-[#334155] border border-[#475569] focus:border-[#1f9e3f] focus:ring-2 focus:ring-[rgba(64,206,66,0.25)]';
  const primaryButtonClass =
    'w-full py-3 rounded-lg font-semibold text-white transition-opacity duration-200 disabled:opacity-60 bg-[#1f9e3f] hover:opacity-90';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#0f172a]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/logo-centro-wellness.png"
            alt=""
            className="w-16 h-16 mx-auto object-contain mb-3"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
          <h1 className="text-3xl font-bold text-white">Centro Wellness</h1>
          <p className="text-[#94a3b8] mt-1">
            {step === 'email' ? 'Accede o crea tu cuenta con tu email' : 'Revisa tu correo'}
          </p>
        </div>

        <div className="rounded-2xl p-8 bg-[#1e293b] border border-[#334155]">
          {error && (
            <div className="mb-4 p-3 rounded-lg flex items-center gap-2 text-sm" style={{ background: '#ef444415', border: '1px solid #ef444455', color: '#fca5a5' }}>
              <AlertIcon size={16} />
              <span>{error}</span>
            </div>
          )}

          {step === 'email' ? (
            <form onSubmit={handleEmailSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#cbd5e1] mb-1.5">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  required
                  autoComplete="email"
                  className={inputClass}
                />
              </div>

              <button type="submit" disabled={submitting} className={primaryButtonClass}>
                {submitting ? 'Enviando código...' : 'Enviarme un código'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleCodeSubmit} className="space-y-5">
              <p className="text-sm text-[#cbd5e1]">
                Hemos enviado un código de {OTP_LENGTH} dígitos a{' '}
                <span className="font-semibold text-white">{email}</span>.
              </p>

              <div>
                <label htmlFor="otp-code" className="block text-sm font-medium text-[#cbd5e1] mb-1.5">
                  Código
                </label>
                <input
                  id="otp-code"
                  name="otp-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern={`\\d{${OTP_LENGTH}}`}
                  maxLength={OTP_LENGTH}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="12345678"
                  required
                  autoFocus
                  className={`${inputClass} text-center text-2xl tracking-[0.5em]`}
                />
              </div>

              <button
                type="submit"
                disabled={submitting || code.length !== OTP_LENGTH}
                className={primaryButtonClass}
              >
                {submitting ? 'Verificando...' : 'Entrar'}
              </button>

              <div className="flex justify-between text-xs">
                <button
                  type="button"
                  onClick={changeEmail}
                  className="text-[#94a3b8] hover:text-white transition-colors"
                >
                  Cambiar email
                </button>
                <button
                  type="button"
                  onClick={() => void requestCode()}
                  disabled={submitting || cooldown > 0}
                  className="text-[#94a3b8] hover:text-white transition-colors disabled:opacity-50 disabled:hover:text-[#94a3b8]"
                >
                  {cooldown > 0 ? `Reenviar código (${cooldown}s)` : 'Reenviar código'}
                </button>
              </div>
            </form>
          )}

          <PrivacyNotice />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
